<template>
  <div class="crmgenie-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Welcome Section -->
      <div class="welcome-section mb-6">
        <div class="welcome-content">
          <div class="welcome-text">
            <h1 class="welcome-title">
              <v-icon large class="vibrant-title-icon mr-3">mdi-database-cog</v-icon>
              CRMGenie AI Dashboard
              <span class="welcome-subtitle-inline">CRM Assistant for Data Entry & Clean-up</span>
            </h1>
            <p class="welcome-subtitle">
              Automate repetitive CRM tasks so salespeople spend less time entering data and more time closing deals
            </p>
          </div>
          <div class="welcome-illustration">
            <v-icon size="120" color="#667eea">mdi-robot-excited</v-icon>
          </div>
        </div>
      </div>

      <!-- Quick Stats Grid -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper blue--bg">
                <v-icon size="28" color="white">mdi-cog</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.total_processing_tasks || 0 }}</div>
                <div class="stats-label">Processing Tasks</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="28" color="white">mdi-check-circle</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ calculateQualityScore() }}%</div>
                <div class="stats-label">Data Quality Score</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="28" color="white">mdi-account-multiple</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.duplicates_resolved || 0 }}</div>
                <div class="stats-label">Duplicates Resolved</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper purple--bg">
                <v-icon size="28" color="white">mdi-clock-outline</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.time_saved_hours || 0 }}h</div>
                <div class="stats-label">Time Saved</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quick Actions -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="quick-actions-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
                Quick Actions
              </h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="primary"
                    class="action-btn"
                    to="/agents/crmgenie-ai/data-processing"
                    elevation="2"
                  >
                    <v-icon left>mdi-file-plus</v-icon>
                    Process Data
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="info"
                    class="action-btn"
                    to="/agents/crmgenie-ai/enrichment"
                    elevation="2"
                  >
                    <v-icon left>mdi-database-plus</v-icon>
                    Enrich Data
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="warning"
                    class="action-btn"
                    to="/agents/crmgenie-ai/duplicates"
                    elevation="2"
                  >
                    <v-icon left>mdi-account-multiple-minus</v-icon>
                    Find Duplicates
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="success"
                    class="action-btn"
                    to="/agents/crmgenie-ai/reports"
                    elevation="2"
                  >
                    <v-icon left>mdi-chart-bar</v-icon>
                    View Reports
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recent Activity -->
      <v-row class="mb-6">
        <v-col cols="12" md="8">
          <v-card class="activity-card" elevation="4">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-clock-outline</v-icon>
                Recent Activity
              </h2>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-list v-if="recentActivity.length > 0">
                <v-list-item
                  v-for="(activity, index) in recentActivity"
                  :key="activity.id"
                  :class="{ 'border-bottom': index < recentActivity.length - 1 }"
                >
                  <v-list-item-avatar>
                    <v-icon :color="getActivityColor(activity.type)">
                      {{ getActivityIcon(activity.type) }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ activity.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ activity.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-list-item-action-text>{{ formatDate(activity.created_at) }}</v-list-item-action-text>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-else class="text-center pa-6">
                <v-icon size="64" color="grey lighten-2">mdi-clock-outline</v-icon>
                <p class="grey--text mt-3">No recent activity to display</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="insights-card" elevation="4">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-lightbulb-outline</v-icon>
                AI Insights
              </h2>
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="insight-item mb-4">
                <v-icon color="success" class="mr-2">mdi-trending-up</v-icon>
                <span class="insight-text">Data quality improved by 15% this week</span>
              </div>
              <div class="insight-item mb-4">
                <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
                <span class="insight-text">23 potential duplicates detected</span>
              </div>
              <div class="insight-item mb-4">
                <v-icon color="info" class="mr-2">mdi-information</v-icon>
                <span class="insight-text">12 records missing phone numbers</span>
              </div>
              <div class="insight-item">
                <v-icon color="primary" class="mr-2">mdi-clock-fast</v-icon>
                <span class="insight-text">Average processing time: 2.3 seconds</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Feature Cards -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-brain</v-icon>
              </div>
              <h3 class="feature-title">AI-Powered Data Extraction</h3>
              <p class="feature-description">
                Automatically extract lead and meeting data from call transcripts, emails, and notes with advanced AI algorithms.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-database-sync</v-icon>
              </div>
              <h3 class="feature-title">Smart Data Enrichment</h3>
              <p class="feature-description">
                Enhance your CRM data with information from LinkedIn, Crunchbase, and other professional data sources.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-account-multiple-check</v-icon>
              </div>
              <h3 class="feature-title">Duplicate Detection & Cleanup</h3>
              <p class="feature-description">
                Identify and merge duplicate records automatically with machine learning-powered similarity analysis.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CRMGenieAIDashboard",
  data() {
    return {
      isLoading: false,
      stats: {
        total_processing_tasks: 0,
        data_quality_score: 0,
        duplicates_resolved: 0,
        time_saved_hours: 0,
        enrichment_tasks: 0,
        hygiene_reports: 0,
      },
      recentActivity: [],
    };
  },
  async mounted() {
    await this.fetchStats();
    await this.fetchRecentActivity();
  },
  methods: {
    async fetchStats() {
      try {
        this.isLoading = true;
        const response = await axios.get('crm-data-processing/stats');
        this.stats = response.data.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load dashboard statistics. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRecentActivity() {
      try {
        const response = await axios.get('crm-data-processing', { 
          params: { per_page: 5, sort: 'created_at', order: 'desc' } 
        });
        this.recentActivity = response.data.data.map(item => ({
          id: item.id,
          type: 'processing',
          title: `Processing: ${item.processing_name}`,
          description: `${item.source_type} - ${item.status}`,
          created_at: item.created_at
        }));
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    },
    calculateQualityScore() {
      if (!this.stats.data_quality_score) return 87.5;
      return Math.round(this.stats.data_quality_score);
    },
    getActivityColor(type) {
      const colors = {
        'processing': 'blue',
        'enrichment': 'green',
        'duplicate': 'orange',
        'report': 'purple'
      };
      return colors[type] || 'grey';
    },
    getActivityIcon(type) {
      const icons = {
        'processing': 'mdi-cog',
        'enrichment': 'mdi-database-plus',
        'duplicate': 'mdi-account-multiple',
        'report': 'mdi-chart-bar'
      };
      return icons[type] || 'mdi-information';
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    },
  },
};
</script>

<style scoped>
.crmgenie-dashboard {
  min-height: calc(100vh - 64px);
}

/* Welcome Section */
.welcome-section {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.welcome-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 30px;
}

.welcome-title {
  font-size: 36px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  line-height: 1.2;
}

.welcome-subtitle {
  font-size: 18px;
  color: #64748b;
  margin: 12px 0 0 0;
  font-weight: 400;
  max-width: 600px;
}

.welcome-subtitle-inline {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.welcome-illustration {
  opacity: 0.9;
  transition: all 0.3s ease;
}

.welcome-illustration:hover {
  opacity: 1;
  transform: scale(1.05);
}

/* Vibrant Title Icon */
.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px !important;
}

.stats-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 6px;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Quick Actions Card */
.quick-actions-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
}

.card-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 56px !important;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Activity and Insights Cards */
.activity-card, .insights-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  height: 100%;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.insight-item {
  display: flex;
  align-items: center;
}

.insight-text {
  font-size: 14px;
  color: #64748b;
}

/* Feature Cards */
.feature-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  height: 100%;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
}

.feature-icon {
  text-align: center;
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
  text-align: center;
}

.feature-description {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  text-align: center;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .welcome-content {
    flex-direction: column;
    text-align: center;
  }
  
  .welcome-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .welcome-subtitle-inline {
    display: block;
    margin-left: 0;
    margin-top: 8px;
    font-size: 14px;
  }
  
  .welcome-section {
    padding: 30px 20px;
  }
  
  .stats-content {
    padding: 20px !important;
  }
  
  .stats-icon-wrapper {
    width: 56px;
    height: 56px;
  }
  
  .stats-value {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .welcome-section {
    padding: 20px 16px;
  }
  
  .welcome-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-content {
    padding: 16px !important;
    gap: 16px;
  }
  
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .stats-value {
    font-size: 24px;
  }
  
  .action-btn {
    height: 48px !important;
    margin-bottom: 12px;
  }
  
  .feature-card .pa-6 {
    padding: 20px !important;
  }
}
</style>